import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";

interface TransportProviderDescriptionProps {
  description?: string | null;
}

export default function TransportProviderDescription({ description }: TransportProviderDescriptionProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = textRef.current;

    if (!element || !description?.trim()) {
      setHasOverflow(false);
      return;
    }

    const measureOverflow = () => {
      if (expanded) {
        return;
      }

      setHasOverflow(element.scrollHeight > element.clientHeight + 1);
    };

    measureOverflow();

    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [description, expanded]);

  if (!description?.trim()) {
    return null;
  }

  return (
    <Box className={`transport-provider-description`}>
      <Box
        ref={textRef}
        className={`transport-provider-description__text ${
          !expanded ? "transport-provider-description__text--collapsed" : ""
        }`}
        dangerouslySetInnerHTML={{ __html: description }}
      />
      {hasOverflow && (
        <Box
          component="button"
          type="button"
          className="transport-provider-description__toggle"
          onClick={() => setExpanded(prev => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? t("showLessLabel") : t("showMoreLabel")}
        </Box>
      )}
    </Box>
  );
}
